import { getAdmissionById } from "@/core/admissions/admission-store";
import { getHospitalRevenueSettings } from "@/core/admin/establishment-store";
import type {
  SurgeryCase,
  SurgeryCaseInput,
  SurgeryCaseStatus,
  SurgeryCaseUpdateInput,
} from "./types";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import {
  apiCreateSurgeryCase,
  apiListSurgeryCases,
  apiSyncSurgeryCase,
} from "@/core/hospitol-api-client";

export type {
  SurgeryCase,
  SurgeryCaseInput,
  SurgeryCaseStatus,
  SurgeryCaseUpdateInput,
} from "./types";
export {
  COMMON_SURGERY_PROCEDURES,
  SURGERY_CASE_STATUS_LABELS,
} from "./types";

const CASES_KEY = "hospitol.surgeryCases";
const SEQ_KEY = "hospitol.surgeryCaseSeq";

function nextCaseNumber(): string {
  const seq = Number(getStorageItem(SEQ_KEY) ?? "0") + 1;
  setStorageItem(SEQ_KEY, String(seq));
  return `CHIR-${String(seq).padStart(5, "0")}`;
}

function normalizeCase(raw: Record<string, unknown>): SurgeryCase {
  return {
    id: String(raw.id ?? ""),
    caseNumber: String(raw.caseNumber ?? ""),
    admissionId: String(raw.admissionId ?? ""),
    patientId: String(raw.patientId ?? ""),
    consultationId:
      raw.consultationId === undefined || raw.consultationId === null
        ? null
        : String(raw.consultationId),
    stayId:
      raw.stayId === undefined || raw.stayId === null ? null : String(raw.stayId),
    procedureName: String(raw.procedureName ?? ""),
    surgeonName: String(raw.surgeonName ?? ""),
    durationMinutes: Math.max(0, Number(raw.durationMinutes ?? 0)),
    procedureFee: Math.max(0, Number(raw.procedureFee ?? 0)),
    operativeNotes: String(raw.operativeNotes ?? ""),
    status: (raw.status as SurgeryCaseStatus) ?? "scheduled",
    scheduledAt: String(raw.scheduledAt ?? new Date().toISOString()),
    completedAt: raw.completedAt ? String(raw.completedAt) : null,
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? new Date().toISOString()),
  };
}

function readCases(): SurgeryCase[] {
  return readJson<Record<string, unknown>[]>(CASES_KEY, []).map(normalizeCase);
}

function writeCases(cases: SurgeryCase[]): void {
  writeJson(CASES_KEY, cases);
}

function replicateToApi(surgeryCase: SurgeryCase): void {
  void apiSyncSurgeryCase(surgeryCase);
}

/** Pull surgery cases from API (WP-SURG1); push local if remote empty. */
export async function syncSurgeryCasesFromApi(): Promise<boolean> {
  const remote = await apiListSurgeryCases();
  if (remote === null) return false;
  if (remote.length > 0) {
    writeCases(remote.map((c) => normalizeCase(c as unknown as Record<string, unknown>)));
    return true;
  }
  for (const c of readCases()) void apiSyncSurgeryCase(c);
  return true;
}

export class SurgeryCaseValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SurgeryCaseValidationError";
  }
}

export function listSurgeryCases(): SurgeryCase[] {
  return readCases().sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

export function getSurgeryCaseById(id: string): SurgeryCase | null {
  return listSurgeryCases().find((c) => c.id === id) ?? null;
}

export function getSurgeryCaseByAdmission(admissionId: string): SurgeryCase | null {
  return (
    listSurgeryCases().find(
      (c) => c.admissionId === admissionId && c.status !== "cancelled",
    ) ?? null
  );
}

export function createSurgeryCase(input: SurgeryCaseInput): SurgeryCase {
  const admission = getAdmissionById(input.admissionId);
  if (!admission) {
    throw new SurgeryCaseValidationError("Admission introuvable.");
  }
  if (!input.procedureName.trim()) {
    throw new SurgeryCaseValidationError("Indiquez l'intervention prévue.");
  }
  const existing = getSurgeryCaseByAdmission(input.admissionId);
  if (existing) {
    throw new SurgeryCaseValidationError("Un dossier bloc existe déjà pour cette admission.");
  }

  const now = new Date().toISOString();
  const defaultFee = getHospitalRevenueSettings().surgeryProcedureFee;
  const surgeryCase: SurgeryCase = {
    id: `schir-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    caseNumber: nextCaseNumber(),
    admissionId: admission.id,
    patientId: admission.patientId,
    consultationId: null,
    stayId: null,
    procedureName: input.procedureName.trim(),
    surgeonName: input.surgeonName?.trim() || "Chirurgien",
    durationMinutes: Math.max(0, input.durationMinutes ?? 0),
    procedureFee: Math.max(0, input.procedureFee ?? defaultFee),
    operativeNotes: input.operativeNotes?.trim() ?? "",
    status: "scheduled",
    scheduledAt: input.scheduledAt ?? now,
    completedAt: null,
    createdAt: now,
    updatedAt: now,
  };

  const cases = readCases();
  cases.unshift(surgeryCase);
  writeCases(cases);
  void apiCreateSurgeryCase({
    admissionId: surgeryCase.admissionId,
    patientId: surgeryCase.patientId,
    procedureName: surgeryCase.procedureName,
    surgeonName: surgeryCase.surgeonName,
    durationMinutes: surgeryCase.durationMinutes,
    procedureFee: surgeryCase.procedureFee,
    operativeNotes: surgeryCase.operativeNotes,
    scheduledAt: surgeryCase.scheduledAt,
  }).then((remote) => {
    if (!remote) {
      replicateToApi(surgeryCase);
      return;
    }
    const list = readCases();
    const idx = list.findIndex((c) => c.id === surgeryCase.id || c.admissionId === surgeryCase.admissionId);
    if (idx >= 0) {
      list[idx] = normalizeCase(remote as unknown as Record<string, unknown>);
      writeCases(list);
    }
  });
  return surgeryCase;
}

export function updateSurgeryCase(
  id: string,
  patch: SurgeryCaseUpdateInput,
): SurgeryCase {
  const cases = readCases();
  const index = cases.findIndex((c) => c.id === id);
  if (index < 0) throw new SurgeryCaseValidationError("Dossier bloc introuvable.");

  const current = cases[index]!;
  if (current.status === "completed" || current.status === "cancelled") {
    throw new SurgeryCaseValidationError("Ce dossier ne peut plus être modifié.");
  }

  const updated: SurgeryCase = {
    ...current,
    procedureName: patch.procedureName?.trim() || current.procedureName,
    surgeonName: patch.surgeonName?.trim() || current.surgeonName,
    durationMinutes:
      patch.durationMinutes != null
        ? Math.max(0, patch.durationMinutes)
        : current.durationMinutes,
    procedureFee:
      patch.procedureFee != null ? Math.max(0, patch.procedureFee) : current.procedureFee,
    operativeNotes: patch.operativeNotes ?? current.operativeNotes,
    status: patch.status ?? current.status,
    scheduledAt: patch.scheduledAt ?? current.scheduledAt,
    updatedAt: new Date().toISOString(),
  };

  cases[index] = updated;
  writeCases(cases);
  replicateToApi(updated);
  return updated;
}

export async function completeSurgeryCase(id: string): Promise<SurgeryCase> {
  const cases = readCases();
  const index = cases.findIndex((c) => c.id === id);
  if (index < 0) throw new SurgeryCaseValidationError("Dossier bloc introuvable.");

  const current = cases[index]!;
  if (current.status === "completed") return current;
  if (current.status === "cancelled") {
    throw new SurgeryCaseValidationError("Dossier annulé — clôture impossible.");
  }
  if (!current.procedureName.trim()) {
    throw new SurgeryCaseValidationError("Intervention requise avant clôture.");
  }

  const now = new Date().toISOString();
  const completed: SurgeryCase = {
    ...current,
    status: "completed",
    completedAt: now,
    updatedAt: now,
  };
  cases[index] = completed;
  writeCases(cases);
  replicateToApi(completed);

  const { autoBillSurgeryCaseOnComplete } = await import(
    "@/core/billing/extended-hospital-billing"
  );
  await autoBillSurgeryCaseOnComplete(completed);

  return completed;
}
